import React, { useState } from 'react';
import {
  Search, Filter, Download, ChevronLeft, ChevronRight,
  Eye, Edit, Trash2, Star, Package, TrendingUp,
  AlertCircle, CheckCircle, Clock, Tag, DollarSign,
  Grid3X3, List, Plus, Image as ImageIcon
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Static Mock Data
  const products = [
    { id: 1, name: 'Monstera Deliciosa', category: 'Indoor Plants', price: 450, stock: 124, status: 'Active', rating: 4.8, seller: 'Green Thumb Nursery', image: '🪴', sales: 342, growth: 15 },
    { id: 2, name: 'Snake Plant', category: 'Air Purifying', price: 299, stock: 85, status: 'Active', rating: 4.5, seller: 'City Gardens', image: '🌿', sales: 278, growth: 8 },
    { id: 3, name: 'Fiddle Leaf Fig', category: 'Indoor Plants', price: 850, stock: 12, status: 'Low Stock', rating: 4.9, seller: 'Green Thumb Nursery', image: '🌳', sales: 156, growth: 22 },
    { id: 4, name: 'Aloe Vera', category: 'Medicinal', price: 150, stock: 0, status: 'Out of Stock', rating: 4.2, seller: 'Herbals Co.', image: '🌵', sales: 89, growth: -2 },
    { id: 5, name: 'Peace Lily', category: 'Air Purifying', price: 350, stock: 45, status: 'Active', rating: 4.6, seller: 'Flora Bliss', image: '🌸', sales: 234, growth: 12 },
    { id: 6, name: 'Jade Plant', category: 'Succulents', price: 280, stock: 67, status: 'Active', rating: 4.7, seller: 'Succulent World', image: '🌱', sales: 198, growth: 5 },
    { id: 7, name: 'Boston Fern', category: 'Ferns', price: 400, stock: 8, status: 'Low Stock', rating: 4.4, seller: 'Fern Factory', image: '🌿', sales: 67, growth: -5 },
    { id: 8, name: 'String of Pearls', category: 'Succulents', price: 320, stock: 23, status: 'Active', rating: 4.9, seller: 'Succulent World', image: '💚', sales: 145, growth: 18 },
  ];

  const categories = ['All Categories', 'Indoor Plants', 'Air Purifying', 'Medicinal', 'Succulents', 'Ferns'];
  const statuses = ['All Status', 'Active', 'Low Stock', 'Out of Stock'];

  const stats = {
    total: products.length,
    active: products.filter(p => p.status === 'Active').length,
    lowStock: products.filter(p => p.status === 'Low Stock').length,
    outOfStock: products.filter(p => p.status === 'Out of Stock').length,
    totalValue: products.reduce((sum, p) => sum + (p.price * p.stock), 0),
    totalSales: products.reduce((sum, p) => sum + p.sales, 0)
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Active': return { icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'Low Stock': return { icon: AlertCircle, color: '#f59e0b', bg: '#fed7aa' };
      case 'Out of Stock': return { icon: AlertCircle, color: '#ef4444', bg: '#fee2e2' };
      default: return { icon: Package, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          product.seller.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || filterCategory === 'All Categories' || product.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || filterStatus === 'All Status' || product.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{typeof value === 'number' ? value.toLocaleString() : value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const ProductCard = ({ product }) => {
    const statusConfig = getStatusConfig(product.status);
    const StatusIcon = statusConfig.icon;
    
    return (
      <div className="product-card">
        <div className="product-image">
          <div className="product-icon">{product.image}</div>
          {product.stock < 15 && <div className="stock-badge low">Low Stock</div>}
        </div>
        <div className="product-details">
          <div className="product-header">
            <h4 className="product-name">{product.name}</h4>
            <div className="product-rating">
              <Star size={14} fill="#f59e0b" color="#f59e0b" />
              <span>{product.rating}</span>
            </div>
          </div>
          <div className="product-meta">
            <span className="category-tag">{product.category}</span>
            <span className="seller-name">By {product.seller}</span>
          </div>
          <div className="product-price">₹{product.price}</div>
          <div className="product-stats">
            <div className="stat">
              <Package size={12} />
              <span>{product.stock} in stock</span>
            </div>
            <div className="stat">
              <TrendingUp size={12} />
              <span>{product.sales} sold</span>
            </div>
            <div className={`growth ${product.growth >= 0 ? 'positive' : 'negative'}`}>
              {product.growth >= 0 ? '+' : ''}{product.growth}%
            </div>
          </div>
          <div className="product-status">
            <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
              <StatusIcon size={12} />
              {product.status}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="partner-products-modern">
      {/* Header */}
      <div className="products-header">
        <div>
          <h1 className="page-title">Products Catalog</h1>
          <p className="page-subtitle">View all products across the platform</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Read-Only Banner */}
      <div className="readonly-banner">
        <Eye size={18} />
        <span>You have read-only access. You can view data but cannot make changes.</span>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Products" value={stats.total} icon={Package} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Active" value={stats.active} icon={CheckCircle} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Low Stock" value={stats.lowStock} icon={AlertCircle} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard title="Total Sales" value={stats.totalSales} icon={TrendingUp} color="#8b5cf6" bgColor="#e9d5ff" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by product name or seller..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            {categories.map(cat => (
              <option key={cat} value={cat === 'All Categories' ? 'all' : cat}>{cat}</option>
            ))}
          </select>
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            {statuses.map(status => (
              <option key={status} value={status === 'All Status' ? 'all' : status}>{status}</option>
            ))}
          </select>
          <div className="view-toggle">
            <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>
              <Grid3X3 size={16} />
            </button>
            <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>{filteredProducts.length} Products Found</span>
      </div>

      {/* Products Grid View */}
      {viewMode === 'grid' && (
        <div className="products-grid">
          {paginatedProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}

      {/* Products List View */}
      {viewMode === 'list' && (
        <div className="table-container">
          <div className="table-responsive">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Seller</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Sales</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.map((product) => {
                  const statusConfig = getStatusConfig(product.status);
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr key={product.id}>
                      <td className="product-cell">
                        <div className="product-icon-small">{product.image}</div>
                        <div>
                          <div className="product-name-cell">{product.name}</div>
                          <div className="product-rating-cell">
                            <Star size={12} fill="#f59e0b" color="#f59e0b" />
                            <span>{product.rating}</span>
                          </div>
                        </div>
                      </td>
                      <td className="category-cell">{product.category}</td>
                      <td className="seller-cell">{product.seller}</td>
                      <td className="price-cell">₹{product.price}</td>
                      <td className={`stock-cell ${product.stock === 0 ? 'out' : product.stock < 15 ? 'low' : ''}`}>
                        {product.stock} pcs
                      </td>
                      <td className="sales-cell">{product.sales} sold</td>
                      <td className="status-cell">
                        <span className="status-badge-small" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                          <StatusIcon size={12} />
                          {product.status}
                        </span>
                       </td>
                      <td className="text-right">
                        <div className="action-buttons">
                          <button className="action-btn view" title="View">
                            <Eye size={16} />
                          </button>
                        </div>
                       </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
          </div>
          <div className="pagination-buttons">
            <button 
              className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button 
                key={i}
                className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredProducts.length === 0 && (
        <div className="empty-state">
          <Package size={64} className="empty-icon" />
          <h3>No products found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}
      <style>{`
        .partner-products-modern {
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }

        /* Header */
        .products-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 4px;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .btn-export {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-export:hover {
          border-color: #10b981;
          color: #10b981;
        }

        /* Read-Only Banner */
        .readonly-banner {
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 12px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          color: #92400e;
          font-size: 13px;
        }

        /* Stats Cards */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          border: 1px solid #e8ecef;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }

        .stat-icon {
          width: 52px;
          height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 16px;
        }

        .stat-info h3 {
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .stat-info span {
          font-size: 12px;
          color: #64748b;
        }

        /* Filters */
        .filters-bar {
          background: white;
          border-radius: 20px;
          padding: 16px 20px;
          margin-bottom: 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
          border: 1px solid #e8ecef;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 350px;
        }

        .search-box svg {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-box input {
          width: 100%;
          padding: 10px 16px 10px 42px;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 14px;
        }

        .filter-group {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }

        .filter-select {
          padding: 8px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          background: white;
          cursor: pointer;
        }

        .view-toggle {
          display: flex;
          gap: 4px;
          background: #f1f5f9;
          padding: 4px;
          border-radius: 12px;
        }

        .view-btn {
          padding: 6px 10px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          color: #64748b;
          transition: all 0.3s ease;
        }

        .view-btn.active {
          background: white;
          color: #10b981;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        /* Results Info */
        .results-info {
          margin-bottom: 20px;
          font-size: 13px;
          color: #64748b;
        }

        /* Products Grid */
        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 24px;
          margin-bottom: 24px;
        }

        .product-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          transition: all 0.3s ease;
          border: 1px solid #e8ecef;
        }

        .product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }

        .product-image {
          position: relative;
          height: 160px;
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .product-icon {
          font-size: 64px;
        }

        .stock-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          padding: 4px 10px;
          background: #f59e0b;
          color: white;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 600;
        }

        .product-details {
          padding: 16px;
        }

        .product-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .product-name {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .product-rating {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #f59e0b;
        }

        .product-meta {
          display: flex;
          gap: 12px;
          margin-bottom: 12px;
          font-size: 11px;
        }

        .category-tag {
          padding: 2px 8px;
          background: #f1f5f9;
          border-radius: 20px;
          color: #475569;
        }

        .seller-name {
          color: #64748b;
        }

        .product-price {
          font-size: 18px;
          font-weight: 700;
          color: #10b981;
          margin-bottom: 12px;
        }

        .product-stats {
          display: flex;
          gap: 16px;
          padding: 12px 0;
          border-top: 1px solid #e8ecef;
          border-bottom: 1px solid #e8ecef;
          margin-bottom: 12px;
        }

        .stat {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #64748b;
        }

        .growth {
          font-size: 11px;
          font-weight: 600;
        }

        .growth.positive {
          color: #10b981;
        }

        .growth.negative {
          color: #ef4444;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        /* Table View */
        .table-container {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid #e8ecef;
          margin-bottom: 24px;
        }

        .products-table {
          width: 100%;
          border-collapse: collapse;
        }

        .products-table th {
          text-align: left;
          padding: 16px 20px;
          background: #f8fafc;
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          border-bottom: 1px solid #e8ecef;
        }

        .products-table td {
          padding: 16px 20px;
          border-bottom: 1px solid #e8ecef;
          font-size: 13px;
        }

        .products-table tr:hover {
          background: #f8fafc;
        }

        .product-cell {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .product-icon-small {
          width: 44px;
          height: 44px;
          background: #f1f5f9;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }

        .product-name-cell {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .product-rating-cell {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #f59e0b;
        }

        .price-cell {
          font-weight: 700;
          color: #10b981;
        }

        .stock-cell {
          font-weight: 500;
        }

        .stock-cell.low {
          color: #f59e0b;
        }

        .stock-cell.out {
          color: #ef4444;
        }

        .status-badge-small {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .action-btn {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-btn.view:hover {
          background: #dbeafe;
          border-color: #3b82f6;
          color: #3b82f6;
        }

        .text-right {
          text-align: right;
        }

        /* Pagination */
        .pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          flex-wrap: wrap;
          gap: 16px;
          border-top: 1px solid #e8ecef;
        }

        .pagination-info {
          font-size: 13px;
          color: #64748b;
        }

        .pagination-buttons {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }

        .page-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .page-btn:hover:not(.disabled) {
          border-color: #10b981;
          color: #10b981;
        }

        .page-btn.disabled, .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-number {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
        }

        .page-number.active {
          background: #10b981;
          border-color: #10b981;
          color: white;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 20px;
        }

        .empty-icon {
          color: #94a3b8;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 8px;
        }

        .empty-state p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .partner-products-modern {
            padding: 16px;
          }
          .stats-grid {
            grid-template-columns: 1fr;
          }
          .filters-bar {
            flex-direction: column;
            align-items: stretch;
          }
          .search-box {
            max-width: none;
          }
          .filter-group {
            flex-direction: column;
          }
          .products-table {
            font-size: 12px;
          }
          .pagination-wrapper {
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Products;